import React, { memo, useState, useRef, useEffect } from "react";
import { createGoogleCustomSearchScript } from "src/components/SponsoredGcse/googleCustomSearchTemplate";
import { gcseClickNotify } from "src/components/SponsoredApiAds/trackers/trackers";
import { SPONSORED_ADS_PROVIDER_IDS } from "src/utils/constants";
import { fireImpressionEvent } from "../../events";
import { v4 as uuidv4 } from "uuid";
import { useStore } from "src/store";
import { useMousePosition } from "../../utils/hooks";

const SponsoredGcse = memo(({ googleCustomAdsParams = "{}", gsceBQData, clickData, sendClientEventsExtraParams }) => {
  const store = useStore(["iframeClickTrackers"]);
  const parsedGoogleCustomAdsParams = JSON.parse(googleCustomAdsParams);
  const [adsRendered, setAdsRendered] = useState(false);
  const intervalRef = useRef(null);
  const gcseShowedTimeRef = useRef(null);
  const [countClicks, setCountClicks] = useState(0);
  const { postbackConfig } = sendClientEventsExtraParams;
  const reqId = uuidv4();
  const secondAdBlock = parsedGoogleCustomAdsParams.secondAdBlock || false;
  const containerIds = ['afscontainer1', 'afscontainer2'];
  window.lastMousePosition = useMousePosition({ active: secondAdBlock });

  useEffect(() => {
    store.set("loadedAds", { haveAdsLoaded: true });
    gsceBQData.reqId = reqId;

    createGoogleCustomSearchScript(parsedGoogleCustomAdsParams, gsceBQData, sendClientEventsExtraParams, clickData.clickNotifyExtraParams.targetWindow);

    const handleAdsLoaded = () => {
      setAdsRendered(true);
      gcseShowedTimeRef.current = Date.now();
      store.set("loadedAds", { haveAdsLoaded: true });

      if (postbackConfig?.type === "facebookPixel") {
        const urlParams = new URLSearchParams(window.location.href);
        const impressionData = {};
        let eventId = null;

        if (!eventId && postbackConfig?.customImpressionData) {
          try {
            const customData = JSON.parse(postbackConfig.customImpressionData);
            if (customData.event_id) {
              eventId = customData.event_id;
            }
            Object.entries(customData).forEach(([key, val]) => {
              if (key !== 'event_id' && !Object.prototype.hasOwnProperty.call(impressionData, key)) {
                impressionData[key] = val;
              }
            });
          } catch (e) {
            console.error('Invalid JSON in customImpressionData', e);
          }
        }
        // Override all 'cx_' parameters from URL
        urlParams.forEach((value, key) => {
          if (key.startsWith('cx_')) {
            if (key === 'cx_event_id') {
              eventId = value;
            } else {
              impressionData[key.replace(/^cx_/, '')] = value;
            }
          }
        });

        // Fire fbq event
        if (Object.keys(impressionData).length > 0 || eventId) {
          if (eventId && window.fbq) {
            window.fbq("track", postbackConfig.impressionEvent, impressionData, { eventID: eventId });
          } else {
            window.fbq && window.fbq("track", postbackConfig.impressionEvent, impressionData);
          }
        } else {
          window.fbq && window.fbq("track", postbackConfig?.impressionEvent);
        }
        // window.fbq && window.fbq("track", postbackConfig?.impressionEvent);

      } else if (postbackConfig?.type === 'facebookConversionAPI') {
        fireImpressionEvent("sponsoredSearchResults2", 0, ["fbclid", "spx"]);
      }
    };

    if (window[`adsLoaded-${containerIds[0]}`] || window[`adsLoaded-${containerIds[1]}`]) {
      handleAdsLoaded();
    } else {
      intervalRef.current = setInterval(() => {
        if (window[`adsLoaded-${containerIds[0]}`] || window[`adsLoaded-${containerIds[1]}`]) {
          handleAdsLoaded();
          clearInterval(intervalRef.current);
        }
      }, 300);
    }

    const messageEventListener = event => {
      try {
        if (typeof event.data === "string" && event.data.startsWith("FSXDC,.aCS")) {
          const iframes = document.querySelectorAll("#afscontainer1 iframe, #afscontainer2 iframe");
          const iframe = Array.from(iframes).find(iframe => iframe.contentWindow === event.source);
          if (iframe && countClicks === 0) {
            let closestAdBlock = "afscontainer1";
            if (secondAdBlock) {
              const firstBottom = document.getElementById("afscontainer1").getBoundingClientRect().bottom;
              const secondTop = document.getElementById("afscontainer2").getBoundingClientRect().top;
              if (Math.abs(secondTop - window.lastMousePosition.y) < Math.abs(firstBottom - window.lastMousePosition.y)) {
                closestAdBlock = "afscontainer2";
              }
            }
            const clickNotifyExtraParams = clickData.clickNotifyExtraParams;
            clickNotifyExtraParams.gcseShowedTime = gcseShowedTimeRef.current;
            clickNotifyExtraParams.rGuid = closestAdBlock;
            clickData.clickNotifyExtraParams.sponsoredSearchResults2Style = parsedGoogleCustomAdsParams.styleId;
            gcseClickNotify(
              { provider: SPONSORED_ADS_PROVIDER_IDS.GOOGLE.name },
              0,
              clickData.data,
              clickNotifyExtraParams,
              reqId,
            );
            setCountClicks((prevCountClicks) => prevCountClicks + 1);
          }
        }
      } catch (e) {
        console.error("Error executing messageEventListener:", e);
      }
    };

    window.addEventListener("message", messageEventListener);

    return () => {
      window.removeEventListener("message", messageEventListener);
      clearInterval(intervalRef.current);
    };
  }, [googleCustomAdsParams]);

  return <div style={{ display: adsRendered ? "block" : "none" }} id="afscontainer1"></div>;
});

export default SponsoredGcse;
